import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, GlassButton, GlassNavItem } from '../../components/ui/GlassComponents';

// Import component tabs
import GeneralSettings from '../../components/team/GeneralSettings';
import WorkHoursSettings from '../../components/team/WorkHoursSettings';
import LeaveTypesSettings from '../../components/team/LeaveTypesSettings';
import BusinessYearSettings from '../../components/team/BusinessYearSettings';
import TeamMembersSettings from '../../components/team/TeamMembersSettings';
import LicensingSettings from '../../components/team/LicensingSettings';
import LeaveManagerSettings from '../../components/team/LeaveManagerSettings';
import EndOfYearSettings from '../../components/team/EndOfYearSettings';
import SSOConfiguration from '../../components/team/SSOConfiguration';
import LeavePoliciesSettings from '../../components/team/LeavePoliciesSettings';
import DepartmentsSettings from '../../components/team/DepartmentsSettings';

const TeamSettings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [teamStats, setTeamStats] = useState({
    activeMembers: 0,
    pendingInvitations: 0,
    planUserLimit: 5
  });
  
  const [team, setTeam] = useState({
    name: '',
    industry: '',
    size: '',
    logo: '',
    workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    workHoursStart: '09:00',
    workHoursEnd: '17:00',
    timeZone: 'UTC',
    leaveTypes: [],
    members: [],
    pendingInvitations: []
  });
  
  const refreshTeamData = async () => {
    setLoading(true);
    try {
      // First try to get the user's data to find their team ID
      console.log('Fetching user profile to get team ID');
      const userResponse = await axios.get('/api/users/profile');
      console.log('User profile response:', userResponse);
      
      // The user profile returns { success: true, user: {...} }
      if (!userResponse.data || !userResponse.data.success || !userResponse.data.user || !userResponse.data.user.Team || !userResponse.data.user.Team.id) {
        throw new Error('No team ID found in user profile');
      }
      
      const teamId = userResponse.data.user.Team.id;
      console.log('Team ID from user profile:', teamId);
      
      // Now fetch the team data using the team ID
      console.log(`Fetching team data from: /api/teams/${teamId}`);
      const response = await axios.get(`/api/teams/${teamId}`);
      console.log('Team data response:', response);
      
      // Handle different response formats
      let teamData;
      if (response.data && response.data.success && response.data.team) {
        teamData = response.data.team;
      } else if (response.data) {
        teamData = response.data;
      } else {
        throw new Error('Invalid team data format');
      }
      
      // Set team data
      setTeam({
        ...teamData,
        // Ensure leaveTypes is always an array
        leaveTypes: teamData.leaveTypes || [],
        // Ensure members is always an array
        members: teamData.members || [],
        // Initialize pending invitations array
        pendingInvitations: []
      });
      
      // Fetch team member statistics using the team data we already have
      try {
        // Use the team data we already have to get active members
        const activeMembers = teamData.members ? teamData.members.length : 0;
        
        // For now, we'll set pending invitations to 0 since we don't have a reliable way to get them
        const pendingInvitations = 0;
        
        console.log('Using team data for statistics');
        console.log('Active members count:', activeMembers);
        console.log('Pending invitations count:', pendingInvitations);
        
        setTeamStats({
          activeMembers,
          pendingInvitations,
          planUserLimit: teamData.planUserLimit || 5
        });
        
        // Try to get more accurate data from the API, but don't block on it
        axios.get('/api/teams/members')
          .then(membersResponse => {
            console.log('Team members API response:', membersResponse.data);
            
            // Separate active members and pending invitations
            const activeMembers = membersResponse.data.filter(member => 
              !member.isPendingInvitation
            );
            
            const pendingInvitations = membersResponse.data.filter(member => 
              member.isPendingInvitation
            );
            
            console.log('API Active members count:', activeMembers.length);
            console.log('API Pending invitations count:', pendingInvitations.length);
            
            // Update team data with active members and pending invitations
            setTeam(prevTeam => ({
              ...prevTeam,
              members: activeMembers,
              pendingInvitations: pendingInvitations
            }));
            
            // Update with more accurate data if available
            setTeamStats({
              activeMembers: activeMembers.length,
              pendingInvitations: pendingInvitations.length,
              planUserLimit: teamData.planUserLimit || 5
            });
          })
          .catch(apiError => {
            console.error('Error fetching team member statistics from API:', apiError);
            // We already set the stats from team data, so no need to do anything here
          });
      } catch (statsError) {
        console.error('Error setting team statistics:', statsError);
        // Set default values as fallback
        setTeamStats({
          activeMembers: 0,
          pendingInvitations: 0,
          planUserLimit: 5
        });
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      console.error('Error details:', error.response ? error.response.data : 'No response data');
      toast.error('Failed to load team settings');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    refreshTeamData();
  }, []);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    
    try {
      let updatedTeam = { ...team };
      console.log('Saving team settings:', updatedTeam);
      console.log('Leave types being saved:', updatedTeam.leaveTypes);
      
      // Handle logo upload if there's a new logo
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        
        try {
          console.log('Uploading logo to /api/teams/upload-logo');
          const uploadResponse = await axios.post('/api/teams/upload-logo', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          console.log('Logo upload response:', uploadResponse);
          updatedTeam.logo = uploadResponse.data.logoUrl;
        } catch (logoError) {
          console.error('Error uploading logo:', logoError);
          console.log('Trying with /api/ prefix');
          const uploadResponse = await axios.post('/api/teams/upload-logo', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          console.log('Logo upload response with /api/ prefix:', uploadResponse);
          updatedTeam.logo = uploadResponse.data.logoUrl;
        }
      }
      
      // If we don't have a team ID, try to get it from the user profile
      if (!team.id) {
        try {
          console.log('No team ID available, fetching from user profile');
          const userResponse = await axios.get('/api/users/profile');
          if (userResponse.data && userResponse.data.success && userResponse.data.user && userResponse.data.user.Team && userResponse.data.user.Team.id) {
            updatedTeam.id = userResponse.data.user.Team.id;
          } else {
            throw new Error('Could not determine team ID');
          }
        } catch (idError) {
          console.error('Error getting team ID:', idError);
          toast.error('Could not determine your team ID');
          throw new Error('Failed to get team ID');
        }
      }
      
      // Update team settings
      console.log('Updating team settings at /api/teams/' + (updatedTeam.id || team.id));
      console.log('Team data being sent:', updatedTeam);
      
      try {
        const updateResponse = await axios.put(`/api/teams/${updatedTeam.id || team.id}`, updatedTeam);
        console.log('Update response:', updateResponse.data);
      } catch (updateError) {
        console.error('Error updating team:', updateError);
        console.error('Error details:', updateError.response ? updateError.response.data : 'No response data');
        throw updateError;
      }
      
      toast.success('Team settings saved successfully');
    } catch (error) {
      console.error('Error saving team settings:', error);
      console.error('Error details:', error.response ? error.response.data : 'No response data');
      console.error('Error status:', error.response ? error.response.status : 'No status');
      toast.error(error.response?.data?.message || 'Failed to save team settings');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Team Settings</h1>
        
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('general')}
              className={`${activeTab === 'general' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              General
            </button>
            <button
              onClick={() => setActiveTab('workHours')}
              className={`${activeTab === 'workHours' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Work Hours
            </button>
            <button
              onClick={() => setActiveTab('leaveTypes')}
              className={`${activeTab === 'leaveTypes' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Leave Types
            </button>
            {user && (user.role === 'team_admin' || user.role === 'super_admin') && (
              <>
                <button
                  onClick={() => setActiveTab('policies')}
                  className={`${activeTab === 'policies' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Policies
                </button>
                <button
                  onClick={() => setActiveTab('departments')}
                  className={`${activeTab === 'departments' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Departments
                </button>
              </>
            )}
            {user && (user.role === 'team_admin' || user.role === 'super_admin') && (
              <button
                onClick={() => setActiveTab('businessYear')}
                className={`${activeTab === 'businessYear' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Business Year
              </button>
            )}
            <button
              onClick={() => setActiveTab('members')}
              className={`${activeTab === 'members' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Team Members
            </button>
            {user && (user.role === 'team_admin' || user.role === 'super_admin') && (
              <button
                onClick={() => setActiveTab('leaveManager')}
                className={`${activeTab === 'leaveManager' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Leave Adjustments
              </button>
            )}
            <button
              onClick={() => setActiveTab('licensing')}
              className={`${activeTab === 'licensing' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Licensing
            </button>
            {user && (user.role === 'team_admin' || user.role === 'super_admin') && (
              <>
                <button
                  onClick={() => setActiveTab('sso')}
                  className={`${activeTab === 'sso' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  SSO
                </button>
                <button
                  onClick={() => setActiveTab('endOfYear')}
                  className={`${activeTab === 'endOfYear' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  End of Year
                </button>
              </>
            )}
          </nav>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'general' && (
          <GeneralSettings 
            team={team} 
            setTeam={setTeam} 
            logoFile={logoFile} 
            setLogoFile={setLogoFile} 
            logoPreview={logoPreview} 
            setLogoPreview={setLogoPreview} 
            saving={saving} 
            handleSubmit={handleSubmit} 
          />
        )}
        
        {activeTab === 'workHours' && (
          <WorkHoursSettings 
            team={team} 
            setTeam={setTeam} 
            saving={saving} 
            handleSubmit={handleSubmit} 
          />
        )}
        
        {activeTab === 'leaveTypes' && (
          <LeaveTypesSettings 
            team={team} 
            setTeam={setTeam} 
            saving={saving} 
            handleSubmit={handleSubmit} 
          />
        )}
        
        {activeTab === 'policies' && (
          <LeavePoliciesSettings />
        )}

        {activeTab === 'departments' && (
          <DepartmentsSettings />
        )}

        {activeTab === 'businessYear' && (
          <BusinessYearSettings 
            team={team} 
            setTeam={setTeam} 
            saving={saving} 
            handleSubmit={handleSubmit} 
          />
        )}
        
        {activeTab === 'members' && (
          <TeamMembersSettings 
            team={team} 
            setTeam={setTeam} 
            refreshTeamData={refreshTeamData} 
          />
        )}
        
        {activeTab === 'licensing' && (
          <LicensingSettings 
            team={team}
            teamStats={teamStats}
          />
        )}
        
        {activeTab === 'leaveManager' && (
          <LeaveManagerSettings 
            team={team}
            refreshTeamData={refreshTeamData}
          />
        )}
        
        {activeTab === 'sso' && (
          <SSOConfiguration />
        )}
        
        {activeTab === 'endOfYear' && (
          <EndOfYearSettings 
            team={team}
            refreshTeamData={refreshTeamData}
          />
        )}
      </div>
    </div>
  );
};

export default TeamSettings;
